// ============================================================================
// Organization & Roster API
// Organization admin API facades
// ============================================================================

import { callBackendCommand, firebaseFunctionCommands, getBackend } from '@/backend';
import type { OrganizationAdminClient, RosterResolutionRequest } from '@/backend/contracts';
import type { RoleTemplateMutationData, RosterPersonMutationData } from '@/backend';
import type {
    EmailDeliveryIssue,
    Organization,
    OrganizationUpdateData,
    RosterPerson,
    RosterPersonInput,
    RosterWorkEntry,
    RoleTemplate,
    RoleTemplateInput,
} from '../org-types';
import { logger } from '@/utils/logger';

function organizationAdminClient(): OrganizationAdminClient {
    const client = getBackend().organizationAdmin;
    if (!client) {
        throw new Error('Organization admin client is unavailable');
    }
    return client;
}

// ============================================================================
// Organization CRUD
// ============================================================================

/**
 * Fetches an organization by its ID.
 */
export async function getOrganization(orgId: string): Promise<Organization | null> {
    try {
        return await organizationAdminClient().getOrganization(orgId);
    } catch (error: unknown) {
        logger.error('Error fetching organization:', error);
        throw error;
    }
}

/**
 * Updates an existing organization.
 */
export async function updateOrganization(
    orgId: string,
    data: OrganizationUpdateData
): Promise<void> {
    try {
        await callBackendCommand(firebaseFunctionCommands.updateOrganization, { orgId, data });
    } catch (error: unknown) {
        logger.error('Error updating organization:', error);
        throw error;
    }
}

// ============================================================================
// Roster CRUD
// ============================================================================

/**
 * Fetches all roster persons for an organization.
 */
export async function getRoster(orgId: string): Promise<RosterPerson[]> {
    try {
        return await organizationAdminClient().listRoster(orgId);
    } catch (error: unknown) {
        logger.error('Error fetching roster:', error);
        throw error;
    }
}

/**
 * Adds a new person to the roster. Returns the new document ID.
 */
export async function addRosterPerson(
    orgId: string,
    person: RosterPersonInput
): Promise<string> {
    try {
        const result = await callBackendCommand(firebaseFunctionCommands.createRosterPerson, { orgId, person });
        return result.personId;
    } catch (error: unknown) {
        logger.error('Error adding roster person:', error);
        throw error;
    }
}

/**
 * Updates an existing roster person.
 */
export async function updateRosterPerson(
    orgId: string,
    personId: string,
    data: RosterPersonMutationData
): Promise<void> {
    try {
        await callBackendCommand(firebaseFunctionCommands.updateRosterPerson, { orgId, personId, data });
    } catch (error: unknown) {
        logger.error('Error updating roster person:', error);
        throw error;
    }
}

/**
 * Deletes a roster person.
 */
export async function deleteRosterPerson(
    orgId: string,
    personId: string
): Promise<void> {
    try {
        await callBackendCommand(firebaseFunctionCommands.deleteRosterPerson, { orgId, personId });
    } catch (error: unknown) {
        logger.error('Error deleting roster person:', error);
        throw error;
    }
}

// ============================================================================
// Assignment
// ============================================================================

/**
 * Assigns a roster person to a production by adding a work history entry.
 */
export async function assignToProduction(
    orgId: string,
    personId: string,
    entry: RosterWorkEntry
): Promise<void> {
    try {
        await callBackendCommand(firebaseFunctionCommands.assignRosterPersonToProduction, {
            orgId,
            personId,
            entry,
        });
    } catch (error: unknown) {
        logger.error('Error assigning to production:', error);
        throw error;
    }
}

// ============================================================================
// Email delivery issues
// ============================================================================

export async function getEmailDeliveryIssues(orgId: string): Promise<EmailDeliveryIssue[]> {
    try {
        return await organizationAdminClient().listEmailDeliveryIssues(orgId);
    } catch (error: unknown) {
        logger.error('Error fetching email delivery issues:', error);
        throw error;
    }
}

export async function clearEmailDeliveryIssue(orgId: string, email: string): Promise<void> {
    try {
        await callBackendCommand(firebaseFunctionCommands.clearEmailDeliveryIssue, { orgId, email });
    } catch (error: unknown) {
        logger.error('Error clearing email delivery issue:', error);
        throw error;
    }
}

// ============================================================================
// Role Templates CRUD
// ============================================================================

/**
 * Fetches all role templates for an organization.
 */
export async function getRoleTemplates(orgId: string): Promise<RoleTemplate[]> {
    try {
        return await organizationAdminClient().listRoleTemplates(orgId);
    } catch (error: unknown) {
        logger.error('Error fetching role templates:', error);
        throw error;
    }
}

/**
 * Adds a new role template. Returns the new document ID.
 */
export async function addRoleTemplate(
    orgId: string,
    template: RoleTemplateInput
): Promise<string> {
    try {
        const result = await callBackendCommand(firebaseFunctionCommands.createRoleTemplate, { orgId, template });
        return result.templateId;
    } catch (error: unknown) {
        logger.error('Error adding role template:', error);
        throw error;
    }
}

/**
 * Updates an existing role template.
 */
export async function updateRoleTemplate(
    orgId: string,
    templateId: string,
    data: RoleTemplateMutationData
): Promise<void> {
    try {
        await callBackendCommand(firebaseFunctionCommands.updateRoleTemplate, { orgId, templateId, data });
    } catch (error: unknown) {
        logger.error('Error updating role template:', error);
        throw error;
    }
}

/**
 * Deletes a role template.
 */
export async function deleteRoleTemplate(
    orgId: string,
    templateId: string
): Promise<void> {
    try {
        await callBackendCommand(firebaseFunctionCommands.deleteRoleTemplate, { orgId, templateId });
    } catch (error: unknown) {
        logger.error('Error deleting role template:', error);
        throw error;
    }
}

export async function getRosterResolutionRequests(
    orgId: string
): Promise<RosterResolutionRequest[]> {
    try {
        return await organizationAdminClient().listRosterResolutionRequests(orgId);
    } catch (error: unknown) {
        logger.error('Error fetching roster resolution requests:', error);
        throw error;
    }
}
